import { formatInstant } from '../../../../utilities/dateTime';
import { startOfMinute, addMinutes } from 'date-fns';
import { legacyLocalDate, legacyLocalLabel, legacyUtcDate } from '../../../../utilities/legacyDatePresentation';
import { parseLocalDate as parseLocalPattern } from '../../../../utilities/dateTime';
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { wallTimeInstant } from "../../../../utilities/calendar/calendarDisplayTime";
import {
  AlertCircle,
  Calendar,
  CalendarClock,
  Clock,
  Loader2,
  X,
} from "lucide-react";

import {
  AttendeeRequest,
  AttendeeResponse,
  UpcommingMeetingDto,
} from "../../../../interfaces/identity/calendar/Events";
import { updateEvent } from "../../../../services/calendarService/EventService";
import CustomInput from "../../calendar/CustomInput";
import TimePicker from "../../calendar/TimePicker";

interface RescheduleMeetingModalProps {
  handleCloseRescheduleModal: () => void;
  rescheduleModal?: boolean;
  authToken: string;
  selectedMeeting: UpcommingMeetingDto;
  displayTimeZone?: string;
  refreshMeeting: () => void | Promise<void>;
}

// RESCHEDULE_TIME_FIX_ROUND3: attendee mapper intentionally uses reduce, NOT flatMap.
const mapAttendeesForUpdate = (
  attendees: AttendeeResponse[] | undefined
): AttendeeRequest[] =>
  (attendees ?? []).reduce<AttendeeRequest[]>((mapped, attendee) => {
    if (attendee.type === "EXTERNAL") {
      if (attendee.email) {
        mapped.push({
          type: "EXTERNAL",
          email: attendee.email,
        });
      }

      return mapped;
    }

    if (attendee.ref) {
      mapped.push({
        type: attendee.type,
        ref: attendee.ref,
      });
    }

    return mapped;
  }, []);

const RescheduleMeetingModal: React.FC<RescheduleMeetingModalProps> = ({
  handleCloseRescheduleModal,
  authToken,
  selectedMeeting,
  refreshMeeting,
  displayTimeZone,
}) => {
  const currentTimeZone = displayTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Match EditEvent's behavior exactly: incoming UTC/ISO timestamps are first
  // displayed in the browser's local timezone, then the selected local date +
  // time are recombined on save and sent with time_zone.
  const initialStart = useMemo(
    () => displayTimeZone ? legacyUtcDate(selectedMeeting.startTime) : legacyLocalDate(selectedMeeting.startTime),
    [selectedMeeting.startTime, displayTimeZone]
  );

  const initialEnd = useMemo(
    () => displayTimeZone ? legacyUtcDate(selectedMeeting.endTime) : legacyLocalDate(selectedMeeting.endTime),
    [selectedMeeting.endTime, displayTimeZone]
  );

  const originalDurationMinutes = useMemo(() => {
    const minutes = Math.trunc((+initialEnd - +initialStart) / 60000);
    return minutes > 0 ? minutes : 30;
  }, [initialEnd, initialStart]);

  const [selectedDate, setSelectedDate] = useState(
    formatInstant(initialStart, "yyyy-MM-dd", currentTimeZone)
  );
  const [startTime, setStartTime] = useState(formatInstant(initialStart, "h:mm a", currentTimeZone));
  const [endTime, setEndTime] = useState(formatInstant(initialEnd, "h:mm a", currentTimeZone));
  const [timeError, setTimeError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const validateDateTimes = () => {
    let start: Date, end: Date;
    try {
      start = wallTimeInstant(selectedDate, startTime, currentTimeZone, selectedMeeting.startTime);
      end = wallTimeInstant(selectedDate, endTime, currentTimeZone, selectedMeeting.endTime);
    } catch (error) { return { valid: false as const, message: error instanceof Error ? error.message : 'Choose a valid calendar time.' }; }

    if (!Number.isFinite(+start) || !Number.isFinite(+end)) {
      return {
        valid: false as const,
        message: "Please select a valid meeting date and time.",
      };
    }

    if ((+startOfMinute(end) <= +startOfMinute(start))) {
      return {
        valid: false as const,
        message: "End time must be after start time.",
      };
    }

    return {
      valid: true as const,
      start,
      end,
    };
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    setTimeError(null);

    // Preserve the meeting's existing duration when the start time changes.
    // The end-time control remains independently editable afterward.
    const parsedStart = (parseLocalPattern(value, "h:mm a") ?? new Date(NaN));
    if (Number.isFinite(+parsedStart)) {
      setEndTime(
        legacyLocalLabel(addMinutes(new Date(parsedStart), originalDurationMinutes), "h:mm a")
      );
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validation = validateDateTimes();
    if (!validation.valid) {
      setTimeError(validation.message);
      toast.error(validation.message);
      return;
    }

    setTimeError(null);
    setIsSaving(true);

    try {
      const payload = {
        id: selectedMeeting.id,
        title: selectedMeeting.title || "",
        description: selectedMeeting.description || "",
        location: selectedMeeting.location || "",
        meetingDetails: selectedMeeting.meetingDetails || "",
        ownerUserID: selectedMeeting.ownerUserID,
        taskId: selectedMeeting.taskId ?? null,

        // IMPORTANT: This intentionally mirrors EditEvent.
        //
        // Do not send the raw TimePicker strings or mutate only the date portion
        // of the original timestamp. Rebuild the complete local wall-clock
        // datetime and tell the API which timezone it belongs to.
        startTime: displayTimeZone ? validation.start.toISOString() : formatInstant(validation.start, "yyyy-MM-dd'T'HH:mm:ss", currentTimeZone),
        endTime: displayTimeZone ? validation.end.toISOString() : formatInstant(validation.end, "yyyy-MM-dd'T'HH:mm:ss", currentTimeZone),
        time_zone: currentTimeZone,

        attendees: mapAttendeesForUpdate(selectedMeeting.attendees),
        online: selectedMeeting.online,
        isSprout: selectedMeeting.isSprout,
      };

      const result = await updateEvent(payload, authToken);

      if (!result?.success) {
        toast.error(result?.message || "Failed to reschedule meeting.");
        return;
      }

      toast.success("Meeting rescheduled successfully.");

      await Promise.resolve(refreshMeeting());
      handleCloseRescheduleModal();
    } catch (error: any) {
      console.error("Failed to reschedule meeting:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred while rescheduling the meeting."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="reschedule-meeting-modal w-full bg-white"
      aria-label="Reschedule meeting"
    >
      <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="reschedule-header-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3E8FF] text-[#7E57C2]">
            <CalendarClock size={20} />
          </div>

          <div className="min-w-0">
            <h2 className="m-0 text-lg font-bold text-gray-900">
              Reschedule Meeting
            </h2>
            <p className="m-0 mt-0.5 truncate text-xs font-medium text-gray-500">
              {selectedMeeting.title}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleCloseRescheduleModal}
          disabled={isSaving}
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Close reschedule meeting"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-6 py-6">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <CustomInput
            icon={Calendar}
            type="date"
            name="rescheduleDate"
            label="Meeting Date"
            value={selectedDate}
            onChange={(event) => {
              setSelectedDate(event.target.value);
              setTimeError(null);
            }}
          />

          <div className="flex flex-col gap-1">
            <TimePicker
              icon={Clock}
              label="Start Time"
              value={startTime}
              onChange={(value) => handleStartTimeChange(value as string)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <TimePicker
              icon={Clock}
              label="End Time"
              value={endTime}
              onChange={(value) => {
                setEndTime(value as string);
                setTimeError(null);
              }}
            />
          </div>
        </div>

        {timeError && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3.5 py-3 text-xs font-semibold text-red-600">
            <AlertCircle size={15} className="mt-0.5 shrink-0" />
            <span>{timeError}</span>
          </div>
        )}

        <div className="reschedule-note mt-6 rounded-xl border border-purple-100 bg-purple-50/40 px-4 py-3 text-xs leading-5 text-gray-600">
          The selected date and times are saved in your current timezone
          <span className="font-semibold text-[#7E57C2]">
            {" "}
            ({currentTimeZone})
          </span>
          .
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/40 px-6 py-4">
        <button
          type="button"
          onClick={handleCloseRescheduleModal}
          disabled={isSaving}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isSaving}
          className="flex min-w-[150px] items-center justify-center gap-2 rounded-lg bg-[#7E57C2] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#6a4aa8] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSaving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Rescheduling…
            </>
          ) : (
            <>
              <CalendarClock size={16} />
              Reschedule
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default RescheduleMeetingModal;
